// Angular
import {APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule} from '@angular/core';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {BrowserModule} from '@angular/platform-browser';
import {registerLocaleData} from '@angular/common';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
// Material
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatIconModule} from '@angular/material/icon';
import {MatListModule} from '@angular/material/list';
import {MatMenuModule} from '@angular/material/menu';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatSnackBarModule} from '@angular/material/snack-bar';
// Auth
import {
  AuthInterceptor as OidcAuthInterceptor,
  AuthModule,
  OidcSecurityService,
  OpenIdConfiguration,
  StsConfigHttpLoader,
  StsConfigLoader
} from 'angular-auth-oidc-client';
// Logging
import {LoggerModule, NGXLogger, NgxLoggerLevel} from 'ngx-logger';
// Portal
import {PortalMessageBroker} from './portal/portal-message-broker';
import {PortalTokenService} from './portal/portal-token.service';
// OpenAPI
import {
  ApiModule as AgrarinformationenApiModule,
  Configuration as AgrarinformationenConfiguration,
} from './openapi/agrarinformationen-openapi';
// App
import {environment} from '../environments/environment';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule} from '@angular/material/tooltip';
import {mergeMap, take, tap} from 'rxjs';
import {ConfigModule} from './config/config.module';
import {ConfigService} from './config/service/config.service';
import {filter, map} from 'rxjs/operators';
import {AuthInterceptor} from './auth/auth.interceptor';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import * as Sentry from '@sentry/angular';
import jwtDecode, {JwtPayload} from 'jwt-decode';
import {MatomoInitializerService, MatomoModule} from 'ngx-matomo-client';
import {ContextInfoService, WellKnownJustFarmingProperties} from './portal/context-info.service';
import {MatDialogModule} from '@angular/material/dialog';
import {StoreRouterConnectingModule} from '@ngrx/router-store';
import {
  AppSkeletonModule,
  BetriebsauswahlModule,
  CONTEXT_INFO,
  ErrorPagesModule,
  IconComponent,
  NGX_LOGGER,
  provideTheme,
  RouterEffects,
} from '@adnova/jf-ng-components';
import {EffectsModule} from '@ngrx/effects';
import {StoreModule} from '@ngrx/store';
import {metaReducers, reducers} from './store/reducers/reducers';
import {NewsEntitiesEffects} from './store/effects/news-entities.effects';
import {NewsEffects} from './modules/routes/news/+store/news.effects';
import {NewsArticleEffects} from './modules/routes/news-article/+store/news-article.effects';
import {OverviewEffects} from './modules/routes/overview/+store/overview.effects';
import {UserViewConfigEffects} from './store/effects/user-view-config.effects';
import {CustomSnackbarComponent} from './modules/components/custom-snackbar/custom-snackbar.component';


registerLocaleData(localeDe, 'de-DE', localeDeExtra);

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    // Angular
    BrowserAnimationsModule,
    BrowserModule,
    // Material
    MatIconModule,
    MatListModule,
    MatSidenavModule,
    MatMenuModule,
    MatButtonModule,
    MatToolbarModule,
    MatCardModule,
    MatSnackBarModule,
    MatDialogModule,
    // JF-NG-Components
    AppSkeletonModule,
    BetriebsauswahlModule,
    ErrorPagesModule,
    IconComponent,
    // Custom Components
    CustomSnackbarComponent,
    // Config
    ConfigModule,
    // Logging
    LoggerModule.forRoot({
      level: NgxLoggerLevel.INFO, // default logging level
      enableSourceMaps: !environment.production,
    }),
    // Auth
    // TODO: Auth ggf. in eigenes Module auslagern
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: (configService: ConfigService) => {
          const configObservable = configService.loaded$
            .pipe(
              take(1),
              map(config => {
                const authConfig: OpenIdConfiguration = {
                  // loaded
                  authority: config.auth.authority,
                  clientId: config.auth.clientId,
                  scope: config.auth.scope,
                  logLevel: config.auth.logLevel,
                  renewTimeBeforeTokenExpiresInSeconds: config.auth.renewTimeBeforeTokenExpiresInSeconds,
                  // predefined
                  redirectUrl: window.location.origin,
                  postLogoutRedirectUri: window.location.origin,
                  responseType: 'code',
                  silentRenew: true,
                  triggerAuthorizationResultEvent: true,
                  ignoreNonceAfterRefresh: true,
                  useRefreshToken: true,
                  // INFO: Bei anpassungen auch AuthInterceptor.matchesSecureRoots() anpassen
                  secureRoutes: [
                    config.agrarinformationen.apiBaseUrl,
                  ],
                };
                return authConfig;
              }),
            );
          return new StsConfigHttpLoader(configObservable);
        },
        deps: [ConfigService],
      },
    }),
    // OpenAPI
    AgrarinformationenApiModule.forRoot(() => new AgrarinformationenConfiguration()),
    // App
    AppRoutingModule,
    StoreModule.forRoot(
      reducers,
      {
        metaReducers,
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictActionSerializability: true,
          strictStateSerializability: true,
        }
      }),
    EffectsModule.forRoot([
      RouterEffects,
      NewsEntitiesEffects,
      UserViewConfigEffects,
      NewsEffects,
      NewsArticleEffects,
      OverviewEffects,
    ]),
    StoreDevtoolsModule.instrument({name: 'Agrarinformationen', maxAge: 25, logOnly: !environment.production, connectInZone: true}),
    StoreRouterConnectingModule.forRoot({}),
    MatTooltipModule,
    MatomoModule.forRoot({
      mode: 'deferred',
      requireConsent: 'tracking',
      // Ohne Router-Anbindung meldet ngx-matomo-client den App-Start sonst selbst als Seitenaufruf
      // (Vorgabe true, auch wenn die Typdoku false nennt). Den Seitenaufruf sendet schon
      // MatomoTrackerService, sonst zählte jeder Start doppelt.
      trackAppInitialLoad: false,
    }),
    IconComponent,
  ],
  exports: [
    AuthModule,
  ],
  providers: [
    provideTheme(),
    OidcAuthInterceptor,
    {
      // INFO: Interceptor um automatisch da OAuth Token zu setzen
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      // INFO: Initializer das Logging
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigService, logger: NGXLogger) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(value => {
              logger.updateConfig({
                ...logger.getConfigSnapshot(),
                level: value.logging.level
              });
            }),
          );
      },
      deps: [ConfigService, NGXLogger],
      multi: true,
    },
    {
      // INFO: Initializer für Sentry Integration
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        contextInfoService: ContextInfoService,
        logger: NGXLogger,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(config => {
              const sentry = config.sentry;
              const info = environment.info;
              if (sentry) {
                Sentry.init({
                  enabled: sentry.enabled,
                  debug: sentry.debug,
                  environment: config.environment,
                  release: info.commit,
                  dsn: sentry.dsn,
                  sampleRate: sentry.sampleRate,
                  // INFO: Zwei Vorgabe-Integrationen der 10er-Linie fliegen heraus, weil die 7er-Linie
                  // sie nicht hatte und der Versionswechsel den Umfang der Meldungen nicht verändern
                  // soll. "BrowserSession" ist der Nachfolger des entfallenen `autoSessionTracking:
                  // false` und würde Session-Envelopes senden; "CultureContext" würde Sprache,
                  // Zeitzone und Kalender des Anwenders mitschicken.
                  // Tracing bleibt aus, weil weder tracesSampleRate/tracesSampler noch eine
                  // Tracing-Integration konfiguriert sind (ehemals `enableTracing: false`).
                  integrations: defaultIntegrations => defaultIntegrations.filter(
                    integration => !['BrowserSession', 'CultureContext'].includes(integration.name),
                  ),
                  // maxBreadcrumbs nur erhöhen, wenn niemals sensiblen Daten mitgeschrieben werden
                  // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/integrations/default/#breadcrumbs
                  maxBreadcrumbs: 0,
                  initialScope: {
                    extra: {
                      'info': info,
                    },
                  },
                  beforeSend: (event) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)) {
                      return event;
                    }
                    logger.debug('Error reporting is disabled, dropping event');
                    return null;
                  },
                  beforeSendTransaction: (event) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (
                      contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS)
                      && contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
                    ) {
                      return event;
                    }
                    logger.debug('Error reporting or analytics are disabled, dropping transaction event');
                    return null;
                  },
                  beforeBreadcrumb: (breadcrumb) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (
                      contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS)
                      && contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
                    ) {
                      return breadcrumb;
                    }
                    logger.debug('Error reporting or analytics are disabled, dropping breadcrumb');
                    return null;
                  },
                });
              }
            }),
          );
      },
      deps: [
        ConfigService,
        ContextInfoService,
        NGXLogger,
      ],
      multi: true,
    },
    {
      // INFO: Angular Sentry ErrorHandler
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        matomoInitializerService: MatomoInitializerService,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(config => {
              const matomoConfig = config.matomo;

              if (
                matomoConfig?.enabled
                && matomoConfig?.trackerUrl
                && matomoConfig?.siteId
              ) {
                matomoInitializerService.initializeTracker({
                  trackerUrl: matomoConfig.trackerUrl,
                  siteId: matomoConfig.siteId,
                });

              }
            }),
          );
      },
      deps: [
        ConfigService,
        MatomoInitializerService,
      ],
      multi: true,
    },
    {
      // INFO: Initializer der die Base-URL setzt
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        agrarinformationenConfiguration: AgrarinformationenConfiguration,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(value => {
              agrarinformationenConfiguration.basePath = value.agrarinformationen.apiBaseUrl;
            }),
          );
      },
      deps: [ConfigService, AgrarinformationenConfiguration],
      multi: true,
    },
    {
      // INFO: Initializer für Portal integration die Base-URL setzt
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        portalMessageBroker: PortalMessageBroker,
        portalTokenService: PortalTokenService,
        oidcSecurityService: OidcSecurityService,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            mergeMap(config => {
              return fromPromise(portalMessageBroker.init(config.portal.targetOrigin))
                .pipe(map(() => config));
            }),
            mergeMap(config => {
              return fromPromise(portalTokenService.init(config.auth.renewTimeBeforeTokenExpiresInSeconds))
                .pipe(map(() => config));
            }),
            tap(() => {
              const runningEmbedded = portalMessageBroker.isRunningInPortal();
              Sentry.setTag('runningEmbedded', runningEmbedded);

              if (runningEmbedded) {
                const decoded = jwtDecode<JwtPayload>(portalTokenService.getAdnovaToken());
                const token = decoded as any;

                const email = token.email;
                const username = token.preferred_username;
                const name = token.name;

                Sentry.setUser({
                  id: decoded.sub,
                  email,
                  username,
                  name,
                });
              } else {
                oidcSecurityService.getAccessToken()
                  .pipe(
                    filter(value => {
                      if (value) {
                        return true;
                      }
                      return false;
                    }),
                  )
                  .subscribe(accessToken => {
                    const decoded = jwtDecode<JwtPayload>(accessToken);
                    const token = decoded as any;

                    const email = token.email;
                    const username = token.preferred_username;
                    const name = token.name;

                    Sentry.setUser({
                      id: decoded.sub,
                      email,
                      username,
                      name,
                    });
                  });
              }
            }),
          );
      },
      deps: [ConfigService, PortalMessageBroker, PortalTokenService, OidcSecurityService],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        disableTooltipInteractivity: true,
        showDelay: 600,
        hideDelay: 150,
      },
    },
    {
      provide: CONTEXT_INFO,
      useClass: ContextInfoService,
    },
    {
      provide: NGX_LOGGER,
      useClass: NGXLogger,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
